import { flow } from 'lodash'
import { Maybe } from '@wix/wix-data-client-wix-code-adt'
import {
  UPLOAD_BUTTON_ROLE,
  SIGNATURE_INPUT_ROLE,
  PAGINATION_ROLE,
  GRID_ROLE,
  BUTTON_ROLE,
} from '@wix/wix-data-client-common-standalone'
import {
  getFilter,
  getSort,
  getDatasetStaticConfig,
  isDatasetConfigured,
  isDatasetReady,
  selectSetFilterCalled,
  getUserFilterInitialData as getUserFilterInitialDataFromState,
  selectCurrentRecordIndex,
} from './rootReducer'
import recordActions from '../records/actions'
import dynamicPagesActions from '../dynamic-pages/actions'
import configActions from '../dataset-config/actions'
import rootActions from './actions'
import configureDatasetStore from './configureStore'
import datasetApiCreator from '../dataset-api/datasetApi'
import eventListenersCreator from '../dataset-events/eventListeners'
import syncComponentsWithState from '../side-effects/syncComponentsWithState'
import { getFieldTypeCreator } from '../data/utils'
import createConnectedComponentsStore from '../connected-components'
import {
  createFilterResolver,
  createValueResolvers,
  hasLocationDependency,
} from '../filter-resolvers'
import wixFormattingCreator from '@wix/wix-code-formatting'
import { createRecordStoreInstance } from '../record-store'
import rootSubscriber from './rootSubscriber'
import dynamicPagesSubscriber from '../dynamic-pages/subscriber'
import createSiblingDynamicPageUrlGetter from '../dynamic-pages/siblingDynamicPageGetterFactory'
import fetchData from './dataFetcher'
import generateRecordFromDefaultComponentValues from '../helpers/generateRecordFromDefaultComponentValues'
import { getCurrentItemIndex, getPageSize, isCursorPaging } from '../helpers/paginationUtils'
import { getComponentsToUpdate } from '../helpers/livePreviewUtils'
import { Dispatcher, errorHandling } from '../helpers'
import { appContext } from '../viewer-app-module/DataBindingAppContext'
import { AppError, CoreError, Trace, reportDatasetActiveOnPage } from '../logger'
import { extractRealDatasetId } from '../helpers/scopedDatasetUtils'
import { createRecordChangeSubscriber } from './recordChangeSubscriber'
import {
  waitForAllScopedDatasetsToBeReady,
  subscribeDetailsDatasetsToMasterOnReady,
} from './dependencies'
import { createDatabindingApi } from '../databinding-api'
import { getComponentsToDatabind } from '../components/getComponentsToDatabind'
import { USER_INPUT_FILTER_ROLES } from '../helpers/constants'
import { getSeoData } from '../seo/getSeoData'
import datasetEntity from '../dataset/dataset-entity'
import { datasetIsControlledByDataSource } from '../helpers/datasetUtils'
import { makeLegacyComponentAdapter } from '../integration/legacy-component-adapter'
import { makeBuilderComponentAdapter } from '../integration/builder-component-adapter'
import { getDatasetSize, getCurrentPageIndex } from '../helpers/paginationUtils'
import {
  BindBehaviorsToComponents,
  dataExpression,
  Dataset,
  datasetEvents,
  RenderDataToComponents,
  Schema,
} from '@wix/cms-viewer-core'
import { adaptRecord } from '../integration/record-adapter'
import { MediaUtils } from '../inverted-dependencies/media-utils'

const createDataset = (controllerFactory, controllerStore) =>
  ({
    shouldCollectSeoData,
    controllerConfig,
    datasetType,
    connections: allConnections,
    connectionsGraph,
    isScoped,
    datasetScope,
    dataProvider,
    dependencyManager,
    seoManager,
    firePlatformEvent,
    dynamicPagesData,
    datasetId,
    fixedRecordId,
    recordStoreService,
    updatedCompIds,
    markControllerAsRendered,
    markDatasetDataFetched,
    renderingRegularControllers,
    modeIsLivePreview,
    modeIsSSR,
    schemasLoading,
    listenersByEvent,
    statusForEditor,
    componentsWithConnectedOptions = new Set(),
    // new contract
    dataset,
  }) => {
    const collectionId = datasetEntity.getCollectionId(dataset)
    const isUserInputFilterConnection = ({ role }) =>
      USER_INPUT_FILTER_ROLES.includes(role)

    const connections = allConnections.filter(
      connection =>
        !isUserInputFilterConnection(connection) || connection.config,
    )

    const isFixedItem = !!fixedRecordId
    const {
      logger,
      platform: {
        user,
        settings: {
          locale,
          currency,
          timezone,
          env: { builder, live },
        },
        utils: { media },
        timers: { queueMicrotask },
        location: { pageUrl, onChange: onLocationChange },
      },
      features,
    } = appContext

    const { setConnectedComponents, getConnectedComponents }
      = createConnectedComponentsStore()
    const unsubscribeHandlers = []

    const { store, subscribe, onIdle } = configureDatasetStore(
      logger,
      datasetId,
    )

    const eventListeners = eventListenersCreator(firePlatformEvent)

    const { fireEvent, register } = eventListeners
    unsubscribeHandlers.push(eventListeners.dispose)

    // Our system has two event listening system.
    // One is internal, meaning those events can be listened only by our own code
    // And the second one is external, meaning these events are listened by wix code, components, etc.
    // dispatcher - internal, eventListeners - external (legacy)
    // TODO: but before dispatcher was introduced, everything was in eventListeners, so it should be refactored
    const dispatcher = new Dispatcher({
      datasetId: extractRealDatasetId(datasetId),
      scopedDatasetId: isScoped ? datasetId : undefined,
      getState: store.getState,
      getSchema: (name = collectionId) => dataProvider.getSchema(name),
    })

    const internalEventsUnsubscibers = dispatcher.subscribe(listenersByEvent)

    unsubscribeHandlers.push(...internalEventsUnsubscibers)

    store.dispatch(
      rootActions.init({
        controllerConfig,
        connections,
        isScoped,
        datasetType,
        pageOffset: datasetEntity.getOffset(dataset),
      }),
    )
    const {
      datasetIsVirtual,
      datasetIsReal,
      datasetIsDeferred,
      datasetIsWriteOnly,
      datasetIsRouter,
      dynamicPageNavComponentsShouldBeLinked,
    } = getDatasetStaticConfig(store.getState())

    const filter = getFilter(store.getState())
    const sort = getSort(store.getState())

    const getSchema = (schemaName = collectionId) => {
      return Maybe.fromNullable(dataProvider.getSchema(schemaName))
    }

    const getUserFilterInitialData = () => {
      return dataProvider.getUserFilterInitialData(datasetId)
    }

    const getFieldType = fieldName => {
      const schema = getSchema(collectionId)
      const referencedCollectionsSchemas
        = dataProvider.getReferencedSchemas(collectionId)
      return schema.chain(s =>
        Maybe.fromNullable(
          getFieldTypeCreator(s, referencedCollectionsSchemas)(fieldName),
        ),
      )
    }

    const valueResolvers = createValueResolvers(
      id => dependencyManager.getDependencyById(id, datasetScope),
      getConnectedComponents,
      getFieldType,
      pageUrl,
    )

    const filterResolver = createFilterResolver({
      valueResolvers,
      getConnectedComponents: () =>
        isDatasetReady(store.getState()) ? getConnectedComponents() : [],
      getFieldType,
      getUserFilterInitialData: () =>
        getUserFilterInitialDataFromState(store.getState()),
      wasSetFilterCalled: flow(() => store.getState(), selectSetFilterCalled),
    })

    const recordStore = createRecordStoreInstance({
      recordStoreService,
      getFilter: flow(() => store.getState(), getFilter),
      getSort: flow(() => store.getState(), getSort),
      getPageSize: () => getPageSize({ state: store.getState() }),
      datasetId,
      filterResolver,
      getSchema,
      fixedRecordId,
    })

    const siblingDynamicPageUrlGetter = dynamicPageNavComponentsShouldBeLinked
      ? createSiblingDynamicPageUrlGetter({
        dataProvider,
        dynamicPagesData,
        collectionName: collectionId,
      })
      : null

    if (dynamicPageNavComponentsShouldBeLinked) {
      subscribe(dynamicPagesSubscriber(siblingDynamicPageUrlGetter))
      store.dispatch(dynamicPagesActions.initialize(connections))
    }

    let resolveDatasetReadyPromise
    let rejectDatasetReadyPromise
    const datasetReadyPromise = new Promise((resolve, reject) => {
      resolveDatasetReadyPromise = resolve
      rejectDatasetReadyPromise = reject
    })
    const onReadyAsync = isScoped ? undefined : () => datasetReadyPromise
    if (!isScoped) {
      register('datasetReady', resolveDatasetReadyPromise)
    }

    unsubscribeHandlers.push(
      recordStoreService
        .map(service =>
          service.onChange(
            createRecordChangeSubscriber(store.getState, store.dispatch),
          ),
        )
        .getOrElse(() => { }),
    )
    const {
      fetchingInitialData,
      resolveUserInputDependency,
      resolveDataSourceDependency,
    } = fetchData({
      dependencyManager,
      shouldFetchInitialData: controllerConfig && !datasetIsWriteOnly,
      recordStore,
      store,
      filter,
      sort,
      datasetIsDeferred,
      modeIsSSR,
      queueMicrotask,
      datasetIsReal,
      collectionId,
      filterResolver,
      getSchemas: () => dataProvider.getSchemas(),
      schemasLoading,
      getUserFilterInitialData,
      datasetId,
    })

    const pageReadyDependencies = [fetchingInitialData]
    const datasetHasExpressions = connections.some(
      c => Object.keys(c?.config?.expressions || {}).length > 0,
    )
    if (datasetHasExpressions && datasetIsReal) {
      pageReadyDependencies.push(dataExpression.initialize(currency))
    }

    const datasetApi = datasetApiCreator({
      store,
      recordStore,
      dataProvider,
      eventListeners,
      controllerStore,
      datasetId,
      datasetType,
      collectionId,
      isFixedItem,
      siblingDynamicPageUrlGetter,
      onIdle,
      dispatcher,
      onReadyAsync,
      resolveDataSourceDependency,
      randomSort: datasetEntity.getRandomSort(dataset),
    })

    const appDatasetApi = datasetApi(false)

    fetchingInitialData.then(() => {
      markDatasetDataFetched()
      const firstRecord = recordStore().fold(
        () => undefined,
        service =>
          service.getSeedRecords().matchWith({
            Empty: () => undefined,
            Results: ({ items }) => items[0],
          }),
      )
      if (firstRecord) {
        store.dispatch(
          recordActions.setCurrentRecord(
            firstRecord,
            datasetEntity.getOffset(dataset),
          ),
        )
      }
    })

    const isScopedDetailsDataset = isScoped && !isFixedItem
    if (isScopedDetailsDataset) {
      dependencyManager
        .getDependenciesByFilter(filter, datasetScope)
        .forEach(({ masterDataset: { api: masterDatasetApi } }) => {
          subscribeDetailsDatasetsToMasterOnReady({
            detailsDatasetApis: [appDatasetApi],
            store,
            masterDatasetApi,
            controllerConfig,
            unsubscribeHandlers,
          })
        })
    }

    const shouldRefreshDataset = () => {
      const currentRecordIndex = getCurrentItemIndex({
        state: store.getState(),
      })
      const isPristine = recordStore().fold(
        () => false,
        service => service.isPristine(currentRecordIndex),
      )

      return isPristine && !datasetIsWriteOnly
    }

    if (shouldCollectSeoData && datasetIsReal && !datasetIsDeferred) {
      Promise.all([fetchingInitialData, schemasLoading]).then(() => {
        let seoData = {}
        try {
          seoData = getSeoData({
            isDpItemDataset: datasetIsRouter,
            recordStore,
            state: store.getState(),
            schema: dataProvider.getSchema(collectionId),
            connections,
          })
        } catch (error) {
          logger.log(
            new AppError(
              `Failed to extract seo data for dataset ${datasetId}`,
              { cause: error },
            ),
          )
        }

        seoManager.submitSeoData(seoData)
      })
    }

    const pageReady = async function (componentFactory) {
      user.onLogin(() => {
        // THIS SHOULD HAPPEN SYNCHRONOUSLY SO TESTS WILL REMAIN MEANINGFUL
        // IF YOU EVER FIND THE NEED TO MAKE IT ASYNC - TALK TO leeor@wix.com
        if (shouldRefreshDataset()) {
          appDatasetApi.refresh()
        }
      })

      const shouldRefreshDatasetOnLocationChange = hasLocationDependency(filter)
      if (shouldRefreshDatasetOnLocationChange) {
        onLocationChange(appDatasetApi.refresh)
      }

      const { components: allComponents, detailsDatasetApis, originalComponents }
        = componentFactory(connections)

      const componentsToUpdate = getComponentsToUpdate({
        updatedCompIds,
        datasetIsReal,
        connectionsGraph,
        components: allComponents,
      })

      setConnectedComponents(componentsToUpdate)

      const allComponentsToDatabind = getComponentsToDatabind(componentsToUpdate, {
        builder,
        datasetId,
        datasetIsReal,
        connectionsGraph,
        getDependencies: () =>
          dependencyManager.getDependenciesByFilter(filter),
      })
      const componentsUnsupportedByCore = allComponentsToDatabind
        .map(component => {
          if (isEligibleForNewCore(component, { builder, features, datasetIsDeferred, modeIsSSR, modeIsLivePreview, controllerConfig })) {
            if (datasetIsDeferred) {
              // When a component connected to a deferred dataset is handled by
              // the core (which, for deferred datasets, happens only during
              // client-side rendering and not during server-side rendering),
              // include it in the legacy logic for initial hiding and
              // subsequent showing once the data has been fetched and rendered.
              //
              // However, to prevent the legacy logic from rendering the
              // component—since rendering is handled by the core—replace the
              // connection config passed to the legacy logic with a dummy
              // config. This way, the legacy logic is responsible only for
              // hiding and showing the component, not for rendering it.

              return {
                ...component,
                connectionConfig: {
                  properties: {
                    '': {
                    // An empty string is used as the property name to prevent
                    // the property from being set on the component (see
                    // `setValue` in `baseComponentAdapter`), because it is a
                    // falsy value.
                      fieldName: '',
                      // `fieldName` must be present; otherwise, an error will
                      // occur in the legacy logic when it sets component props.
                    },
                  },
                },
              }
            } else {
              return null
            }
          } else {
            return component
          }
        })
        .filter(component => component !== null)

      subscribeDetailsDatasetsToMasterOnReady({
        detailsDatasetApis,
        masterDatasetApi: appDatasetApi,
        dependencyManager,
        controllerConfig,
        unsubscribeHandlers,
      })

      if (datasetIsReal) {
        await schemasLoading
      }

      resolveUserInputDependency()

      if (datasetIsControlledByDataSource(dataset)) {
        await fetchingInitialData
      }
      if (
        // router dataset, without router data
        !isDatasetConfigured(store.getState())
        // !controllerConfig.dataset.collectionName ||
        // removed collection, nothing to bind.
        || !dataProvider.hasSchema(controllerConfig.dataset.collectionName)
      ) {
        fetchingInitialData.then(() => {
          markControllerAsRendered()
          dependencyManager.resolveDependants(datasetId)
          store.dispatch(configActions.setIsDatasetReady(true))
          fireEvent('datasetReady')
        })

        return Promise.resolve()
      }

      const wixFormatter = !locale
        ? null
        : wixFormattingCreator({ locale, timeZone: timezone })
      const databindingApi = createDatabindingApi({
        components: componentsUnsupportedByCore,
        context: {
          connections,
          recordStore,
          dispatch: store.dispatch,
          getState: store.getState,
          datasetApi: appDatasetApi,
          eventListeners,
          dispatcher,
          getFieldType,
          getSchema,
          controllerFactory,
          controllerStore,
          modeIsLivePreview,
          wixFormatter,
          datasetId,
        },
      })

      const {
        formatter, dataStore, errorReporter, mediaUtils,
      } = makeCoreDependencies({
        wixFormatter,
        getSchema,
        recordStore,
        logger,
        dataProvider,
        wixMedia: media,
      })

      const tryCoreIntegration = (integrate, message) => {
        try {
          return integrate()
        } catch (error) {
          logger.log(CoreError.of(message, { cause: error }))
          throw error
        }
      }

      const componentsSupportedByCore = tryCoreIntegration(
        () => {
          const originalComponentsSupportedByCore = getComponentsSupportedByCore(
            allComponentsToDatabind,
            originalComponents,
            { builder, features, datasetIsDeferred, modeIsSSR, modeIsLivePreview, controllerConfig },
          )

          // TODO: Can be a singleton for all controllers.
          const emptyValueReporter = {
            reportComponentPropertiesConnectedToEmptyFields: ({
              componentId,
              propertyNameToEmptyFieldKey,
              primaryPropertyIsConnectedToEmptyField,
            }) => {
              statusForEditor.updateComponentPropertiesConnectedToEmptyFields(
                componentId,
                {
                  propertyToField: propertyNameToEmptyFieldKey,
                  includesPrimaryProperty: primaryPropertyIsConnectedToEmptyField,
                },
              )
            },
          }

          const makePlatformComponentAdapter = builder ? makeBuilderComponentAdapter : makeLegacyComponentAdapter
          const adaptPlatformComponent = makePlatformComponentAdapter({
            emptyValueReporter,
            environment: modeIsLivePreview ? 'editor' : 'viewer',
            schema: dataProvider.getSchema(collectionId),
            schemas: dataProvider.getReferencedSchemas(collectionId),
            mediaUtils,
          })

          return originalComponentsSupportedByCore
            .map(platformComponent => {
              try {
                return tryCoreIntegration(
                  () => adaptPlatformComponent(platformComponent),
                  'CVCI - component adaption failed',
                )
              } catch {
                return null
              }
            })
            .filter(adaptedComponent => adaptedComponent !== null)
        },
        'CVCI - components adaption failed',
      )

      const {
        executeBindBehaviorsToComponents,
        executeRenderDataToComponents,
      } = (() => {
        if (componentsSupportedByCore.length === 0) {
          return {
            executeBindBehaviorsToComponents: () => {},
            executeRenderDataToComponents: () => {},
          }
        }

        const renderDataToComponents = RenderDataToComponents({
          formatter,
          dataStore,
          errorReporter,
          dataExpression,
          mediaUtils,
        })
        const bindBehaviorsToComponents = BindBehaviorsToComponents({
          api: {
            renderNextRecord: appDatasetApi.next,
            renderPrevRecord: appDatasetApi.previous,
            renderNextPage: appDatasetApi.nextPage,
            renderPrevPage: appDatasetApi.previousPage,
            saveRecord: appDatasetApi.save,
          },
          datasetEvents,
          errorReporter,
        })

        const executeBindBehaviorsToComponents = () => {
          const dataset = tryCoreIntegration(
            () => adaptDatasetForCore({
              datasetId,
              controllerConfig,
              recordStore,
              store,
            }),
            'CVCI - bindBehaviorsToComponents dataset adapation failed',
          )

          tryCoreIntegration(
            () => bindBehaviorsToComponents({
              dataset,
              components: componentsSupportedByCore,
            }),
            'CVCI - bindBehaviorsToComponents failed',
          )
        }

        const executeRenderDataToComponents = ({ phase }) => {
          const dataset = tryCoreIntegration(
            () => adaptDatasetForCore({
              datasetId,
              controllerConfig,
              recordStore,
              store,
            }),
            'CVCI - renderDataToComponents dataset adapation failed',
          )

          tryCoreIntegration(
          // TODO:migration:phase1: This integration doesn't work inside of repeater, because repeater handles his case in record store
          // const items = dataProvider.integration.getRecords({
          //   datasetId,
          //   collectionId: controllerConfig.dataset.collectionName,
          //   includes: controllerConfig.dataset.includes,
          // }).items,
            () => renderDataToComponents({
              dataset,
              components: componentsSupportedByCore,
              updatedComponentIds: modeIsLivePreview ? updatedCompIds : [],
            }),
            `CVCI - renderDataToComponents failed - phase ${phase}`,
          )
        }

        return {
          executeBindBehaviorsToComponents,
          executeRenderDataToComponents,
        }
      })()

      subscribeCoreToOldDatasetEvents({
        register,
        dispatcher,
        datasetEvents,
        dataset: adaptDatasetForCore({
          datasetId,
          controllerConfig,
          recordStore,
          store,
        }),
      })

      subscribe(
        rootSubscriber(
          recordStore,
          databindingApi,
          getFieldType,
          eventListeners.executeHooks,
          datasetId,
          componentsUnsupportedByCore,
          fireEvent,
          dispatcher,
          executeRenderDataToComponents,
        ),
      )

      unsubscribeHandlers.push(
        syncComponentsWithState({
          store,
          logger,
          recordStore,
          datasetId,
          components: componentsUnsupportedByCore,
          randomSort: datasetEntity.getRandomSort(dataset),
        }),
      )

      const defaultRecord = generateRecordFromDefaultComponentValues(
        componentsUnsupportedByCore.filter(
          ({ id, nickname, role }) =>
            ![UPLOAD_BUTTON_ROLE, SIGNATURE_INPUT_ROLE].includes(role) &&
            !componentsWithConnectedOptions.has(id) &&
            !componentsWithConnectedOptions.has(nickname),
        ),
        getFieldType,
      )

      store.dispatch(recordActions.setDefaultRecord(defaultRecord))
      if (datasetIsWriteOnly) {
        await store.dispatch(recordActions.initWriteOnly(datasetIsVirtual))
      }

      if (datasetIsDeferred) {
        databindingApi.hideAll()

        if (modeIsSSR) {
          databindingApi.clearAll()
        }
      }

      const pageReadyResult = Promise.all(pageReadyDependencies).then(
        async () => {
          if (datasetIsDeferred) {
            await renderingRegularControllers
          }

          if (!modeIsSSR) {
            try {
              reportDatasetActiveOnPage(
                store.getState(),
                connections,
                datasetType,
                datasetIsVirtual,
                datasetId,
                getSchema(collectionId),
              )
            } catch (err) {
              logger.log(
                new AppError('Failed to report dataset active BI', {
                  cause: err,
                }),
              )
            }
          }

          if (!modeIsLivePreview) {
            executeBindBehaviorsToComponents()
          }
          dispatcher.dispatch('dataInitialLoadSuccess')
          executeRenderDataToComponents({ phase: 'pageReady' })

          await databindingApi.bindAll()
          if (datasetIsReal) {
            await waitForAllScopedDatasetsToBeReady(controllerStore)
          }
          if (datasetIsDeferred) {
            databindingApi.showAll()
          }
          dependencyManager.resolveDependants(datasetId)
          store.dispatch(configActions.setIsDatasetReady(true))

          if (live && doesDatasetRequireSeoTags(dataset, connections)) {
            await seoManager.renderDatasetSeoTags({
              totalPages: datasetEntity.isItemPageRouter(dataset)
                ? 1
                : appDatasetApi.getTotalPageCount(store.getState()),
              currentPage: appDatasetApi.getCurrentPageIndex(store.getState()),
              queryParam: datasetEntity.isRouter(dataset)
                ? 'dynamic_page'
                : `${datasetId}_page`,
            })
          }
          fireEvent('datasetReady')
        },
      )

      if (datasetIsDeferred) {
        markControllerAsRendered()

        return Promise.resolve()
      } else {
        pageReadyResult.then(markControllerAsRendered)

        return pageReadyResult
      }
    }

    const userCodeDatasetApi = datasetApi(true)

    const dispose = () => {
      unsubscribeHandlers.forEach(h => h())
    }

    const finalPageReady = datasetIsVirtual
      ? pageReady
      : (...args) =>
        logger.log(new Trace('dataset/pageReady', () => pageReady(...args)))
    return {
      pageReady: errorHandling(finalPageReady, e => {
        rejectDatasetReadyPromise(e)
        return logger.logError(e, 'Dataset pageReady callback failed', {
          datasetId,
        })
      }),
      staticExports: userCodeDatasetApi,
      dispose,
      api: appDatasetApi,
      userFilterInitialDataPromise: fetchingInitialData.then(
        ([_seedData, userFilterInitialData]) => userFilterInitialData,
      ),
    }
  }

export default createDataset

function doesDatasetHavePaginationConnection(connections) {
  return connections.some(
    connection =>
      connection?.role === PAGINATION_ROLE
      || connection?.role === GRID_ROLE
      // special case: it could be BUTTON_ROLE with one of pagination actions
      || (connection?.role === BUTTON_ROLE
        && ['loadMore', 'next', 'nextPage', 'previous', 'previousPage'].includes(
          connection?.config?.events?.onClick?.action,
        )),
  )
}

function doesDatasetRequireSeoTags(dataset, connections = []) {
  return (
    datasetEntity.isReal(dataset)
    && !datasetEntity.isDeferred(dataset)
    && !datasetEntity.isWriteOnly(dataset)
    && !datasetEntity.isPaginationCursor(dataset)
    && doesDatasetHavePaginationConnection(connections)
  )
}

const makeCoreDependencies = ({ wixFormatter, getSchema, recordStore, logger, dataProvider, wixMedia }) => {
  const formatter = {
    formatDateTime: (value, format, options) =>
      wixFormatter?.formatDateTime(new Date(value), format, options) ?? value,
  }
  const dataStore = {
    readSchema: (collectionId) => getSchema(collectionId)
      .chain((schema) => schema ? Maybe.Just(Schema.make(schema)) : Maybe.Nothing())
      .getOrElse(null),
    readSchemas: (collectionIds) => Object
      .entries(dataProvider.getSchemas())
      .reduce((acc, [id, schema]) => collectionIds.includes(id) ? [...acc, Schema.make(schema)] : acc, []),
    readRecords: () => recordActions.getAllScopeRecordsSync(recordStore).items.map(adaptRecord),
  }
  const errorReporter = {
    report: (error) => {
      if (!(error instanceof CoreError)) {
        logger.log(CoreError.of('Runtime exception', { cause: error }))
      }
      return logger.log(error)
    },
  }
  const mediaUtils = MediaUtils.of(wixMedia)

  return {
    formatter,
    dataStore,
    mediaUtils,
    errorReporter,
  }
}

const adaptDatasetForCore = ({ datasetId, controllerConfig, recordStore, store }) => Dataset.make({
  id: datasetId, // Keep in mind it could be a virtual dataset id
  collectionId: controllerConfig.dataset.collectionName,
  mode: controllerConfig.dataset.readWriteType,
  pagination: Dataset.makePagination({
    type: isCursorPaging({ recordStore }) ? 'cursor' : 'offset',
    offset: (getCurrentPageIndex({ state: store.getState() }) - 1) * controllerConfig.dataset.pageSize,
    pageSize: controllerConfig.dataset.pageSize,
    total: getDatasetSize({ recordStore })?.total ?? 0,
  }),
  currentRecordIndex: selectCurrentRecordIndex(store.getState()),
})

const subscribeCoreToOldDatasetEvents = ({ register, dispatcher, datasetEvents, dataset }) => {
  register('datasetReady', () => datasetEvents.emit(dataset, 'ready'))
  register('datasetError', () => datasetEvents.emit(dataset, 'error'))
  register('beforeSave', () => datasetEvents.emit(dataset, 'recordSaveAttempt'))
  register('afterSave', (record, afterSaveRecord) =>
    datasetEvents.emit(dataset, 'recordSaveSuccess', {
      nextRecord: afterSaveRecord,
      prevRecord: record,
    }))
  register('currentIndexChanged', (nextIndex) =>
    datasetEvents.emit(dataset, 'currentRecordSwitch', { nextIndex }))
  register('itemValuesChanged', (oldRecord, newRecord) =>
    datasetEvents.emit(dataset, 'recordChange', {
      nextRecord: newRecord,
      prevRecord: oldRecord,
    }))
  dispatcher.subscribe('datasetSaveError', () => datasetEvents.emit(dataset, 'recordSaveFailure'))
  dispatcher.subscribe('dataInitialLoadSuccess', () => datasetEvents.emit(dataset, 'dataInitialLoadSuccess'))
  dispatcher.subscribe('dataLoadSuccess', () => datasetEvents.emit(dataset, 'dataLoadSuccess'))
}

const getComponentsSupportedByCore = (allComponentsToDatabind, originalComponents, { builder, features, datasetIsDeferred, modeIsSSR, modeIsLivePreview, controllerConfig }) => {
  const allComponentIdsToDatabind = allComponentsToDatabind.map(({ id }) => id)
  const originalComponentsSupportedByCore = originalComponents.filter((component) =>
    allComponentIdsToDatabind.includes(component.uniqueId)
    && Boolean(component.connectionConfig)
    && isEligibleForNewCore(component, { builder, features, datasetIsDeferred, modeIsSSR, modeIsLivePreview, controllerConfig }),
  )
  return originalComponentsSupportedByCore
}

const isEligibleForNewCore = (component, { builder, features, datasetIsDeferred, modeIsSSR, modeIsLivePreview, controllerConfig }) => {
  const { role, connectionConfig } = component

  return builder
    ? ['textRole', 'imageRole', 'siteButtonRole'].includes(role)
    : features.enableNewCore
      && (!datasetIsDeferred || !modeIsSSR)
      // Components connected to a deferred dataset are hidden and cleared
      // during server-side rendering, so they do not need to be rendered.
      //
      // Note that when components connected to a deferred dataset are rendered
      // on the client, a workaround is used to apply the legacy logic for
      // initial hiding and subsequent showing once the data has been fetched
      // and rendered (see the comment for `componentsUnsupportedByCore`).
      && (features.newCoreLivePreview || !modeIsLivePreview)
      && isRoleSupportedByCore(role, { features })
      && !hasUnsupportedEvents(connectionConfig, { features })
      && (
        role === 'siteButtonRole'
        // Skip further feature checks for a button.
        || (
          (features.newCoreReferences || !controllerConfig.dataset.includes?.length)
          && (features.newCoreFormatters || !hasFormat(connectionConfig))
          && (features.newCoreExpressions || !hasExpressions(connectionConfig))
        )
      )
}

const isRoleSupportedByCore = (role, { features }) => [
  features.newCoreTextStaticBinding && 'textRole',
  features.newCoreImageStaticBinding && 'imageRole',
  features.newCoreSiteButtonStaticBinding && 'siteButtonRole',
]
  .filter(Boolean)
  .includes(role)
const hasUnsupportedEvents = (connection, { features }) => {
  const supportedEvents = features.newCoreClickEventActions1 ? ['onClick'] : []
  const supportedClickActions = features.newCoreClickEventActions1 ? ['next', 'previous', 'nextPage', 'previousPage', 'save'] : []
  const events = connection.events ? Object.keys(connection.events) : []
  const clickAction = connection.events?.onClick?.action
  return events.some(event => !supportedEvents.includes(event))
    || clickAction && !supportedClickActions.includes(clickAction)
}
const hasFormat = (connection) => Object.values(connection?.properties ?? {})
  .some((value) => Boolean(value?.format))
const hasExpressions = (connection) => Object.entries(connection?.expressions || {}).length > 0
